import React from "react";
import { HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { findGlossaryTerm } from "@/lib/glossary";

/**
 * HelpTooltip
 * ------------------------------------------------------------------
 * Small "?" icon that shows the glossary `short` definition on hover
 * and links through to /glossary#<id> on click.
 *
 * Usage:
 *   <HelpTooltip termId="yield-on-cost" />
 *   <HelpTooltip termId="pe-ratio" size="xs" />
 *   <HelpTooltip termId="pe-ratio" inline /> // inline within text
 */
const SIZE_MAP = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
};

const HelpTooltip = ({ termId, size = "sm", className = "", testId, label }) => {
  const term = findGlossaryTerm(termId);
  const iconClass = SIZE_MAP[size] || SIZE_MAP.sm;

  // Fallback to a generic help if term doesn't exist (defensive)
  const tooltipText = term?.short || label || "Open glossary for definition";
  const href = term ? `/glossary#${term.id}` : "/glossary";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={href}
            data-testid={testId || `help-tooltip-${termId || "generic"}`}
            aria-label={
              term ? `Help: ${term.term}` : "Open glossary"
            }
            className={`inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ml-1 align-middle ${className}`}
            onClick={(e) => {
              // Allow tooltip to dismiss; prevent propagation to parent click handlers
              e.stopPropagation();
            }}
          >
            <HelpCircle className={iconClass} />
          </Link>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="max-w-xs text-xs leading-relaxed bg-popover text-popover-foreground border border-border shadow-md"
        >
          {term ? (
            <div className="space-y-1">
              <div className="font-semibold">{term.term}</div>
              <div className="text-muted-foreground">{term.short}</div>
              <div className="text-[10px] text-muted-foreground/70 italic pt-0.5">
                Click for full definition
              </div>
            </div>
          ) : (
            <div>{tooltipText}</div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default HelpTooltip;
